/**
 * Molecule · Device — behaviour for autoplaying screen recordings.
 *
 * A looping hero video is motion the viewer never asked for. When the OS says
 * "reduce motion" we stop it on the poster frame and hand back native controls
 * so it stays watchable on purpose rather than playing at them.
 *
 * Returns a cleanup that releases the media-query listener, matching
 * initNavbar's contract so every behaviour module in the library detaches the
 * same way.
 */
export function initDeviceMedia(scope: ParentNode = document): () => void {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const media = scope.querySelectorAll<HTMLVideoElement>('.fc-device__media[autoplay]');
  if (!media.length) return () => {};

  const apply = () => {
    media.forEach((video) => {
      if (reduced.matches) {
        video.autoplay = false;
        video.controls = true;
        video.pause();
        // Back to the poster frame, so the still is a deliberate one.
        try { video.currentTime = 0; } catch { /* not seekable yet */ }
      } else {
        video.controls = false;
        video.autoplay = true;
        void video.play().catch(() => {
          // Autoplay refused (low-power mode, data saver). The poster stands in
          // and the viewer can start it themselves.
          video.controls = true;
        });
      }
    });
  };

  apply();
  reduced.addEventListener('change', apply);

  return () => reduced.removeEventListener('change', apply);
}

/**
 * Molecule · Device — "see it full size".
 *
 * The inline recording is muted, loops, and carries no controls: it is there to
 * be glanced at. This opens the same file in a native <dialog>, big, with the
 * player's own controls, for anyone who wants to actually watch it.
 *
 * The dialog's <video> is CLONED from the one on the page, so the sources and
 * the poster are declared exactly once in the markup. Cloning also means the
 * dialog costs nothing until it is opened — the clone shares the browser's
 * cache with the inline video, so no second download.
 *
 * The trigger ships `hidden` and is revealed here: without JavaScript it does
 * nothing, so without JavaScript it should not be on the page.
 *
 * Returns a cleanup, matching every other behaviour module in the library.
 */
export function initDeviceDialog(scope: ParentNode = document): () => void {
  const triggers = scope.querySelectorAll<HTMLButtonElement>('.fc-device__expand');
  if (!triggers.length) return () => {};

  const cleanups: Array<() => void> = [];

  triggers.forEach((trigger) => {
    // Scoped to the figure, not the bezel: the trigger sits below the laptop
    // body, so it is the screen's sibling rather than its child.
    const device = trigger.closest('.fc-device');
    const source = device?.querySelector<HTMLVideoElement>('.fc-device__media');
    // No <dialog> support means no graceful way to show this; leave the button
    // hidden rather than wiring a control that cannot open anything.
    if (!source || typeof HTMLDialogElement === 'undefined') return;

    const dialog = document.createElement('dialog');
    dialog.className = 'fc-video-dialog';

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'fc-video-dialog__close';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML =
      '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2.2" stroke-linecap="round" aria-hidden="true">' +
      '<path d="M6 6l12 12M18 6L6 18" /></svg>';

    const video = source.cloneNode(true) as HTMLVideoElement;
    video.className = 'fc-video-dialog__media';
    video.controls = true;
    video.autoplay = false;
    video.loop = false;
    video.muted = false;
    video.removeAttribute('aria-label');

    dialog.append(close, video);
    document.body.append(dialog);

    const open = () => {
      dialog.showModal();
      void video.play().catch(() => {
        // Refused (a browser that will not start unmuted media without a
        // gesture it recognises). The controls are right there.
      });
    };

    // Leaving a video playing behind a closed dialog is the one thing a modal
    // player must never do.
    const onClose = () => {
      video.pause();
      video.currentTime = 0;
    };

    // The backdrop is the dialog's own box, so a click that lands on the
    // element itself — not on the video or the button — is a click outside.
    const onBackdrop = (event: MouseEvent) => {
      if (event.target === dialog) dialog.close();
    };

    // The screen itself opens it too. A video that visibly plays invites a
    // click, and landing on nothing is a small broken promise. It stays a
    // convenience, not the control: the button below is what a keyboard and a
    // screen reader use, so the video needs no role and no tabindex.
    //
    // Guarded on `controls` because initDeviceMedia hands them back under
    // reduce-motion — and there a click belongs to play/pause, not to us.
    const onScreenClick = () => { if (!source.controls) open(); };

    trigger.addEventListener('click', open);
    source.addEventListener('click', onScreenClick);
    close.addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', onClose);
    dialog.addEventListener('click', onBackdrop);

    trigger.hidden = false;
    // Only now does the screen become clickable, so the cursor never promises
    // something that has not been wired up.
    source.classList.add('is-openable');

    cleanups.push(() => {
      trigger.removeEventListener('click', open);
      source.removeEventListener('click', onScreenClick);
      source.classList.remove('is-openable');
      dialog.removeEventListener('close', onClose);
      dialog.removeEventListener('click', onBackdrop);
      dialog.remove();
      trigger.hidden = true;
    });
  });

  return () => cleanups.forEach((fn) => fn());
}
